import { useObjectRef } from '@react-aria/utils';
import { createContext, forwardRef, useContext } from 'react';
import type { ContextValue } from 'react-aria-components';
import {
  Button as AriaButton,
  ButtonContext as AriaButtonContext,
  Provider,
} from 'react-aria-components';

import { IconContext } from '@infinex/ui-icons/context';
import {
  cn,
  pressScale,
  useStyleProps,
  useContextProps,
} from '@infinex/ui-styles';
import { isReactText } from '@infinex/ui-utils/react';

import { buttonVariants, textSizeClasses } from './buttonVariants';
import type { ButtonProps } from './types';

import { centerBaseline, ImageContext, Text, TextContext } from '../content';
import { ChainEmblemContext, EmblemContext } from '../emblem';
import { ProgressCircle } from '../progress';
import { useSurfaceLayer } from '../surface';

export const ButtonContext =
  createContext<ContextValue<ButtonProps, HTMLButtonElement>>(null);

/**
 * Buttons allow users to perform an action. They have multiple styles for
 * various needs, and are ideal for calling attention to where a user needs to
 * do something in order to move forward in a flow.
 *
 * [Taurus docs: `Button`](https://ui-sb.app.infinex.dev/?path=/docs/components-button--docs)
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(props, ref) {
    [props, ref] = useContextProps(props, ref, ButtonContext);
    const domRef = useObjectRef<HTMLButtonElement>(ref);
    const { className, style } = useStyleProps(props);
    const { children, prominence, size = 'medium', staticColor } = props;

    const layer = useSurfaceLayer();
    const contextValues = getButtonContextValues(props);

    // While `isPending`, interaction is deactivated by omitting handlers
    // (managed by react-aria-components). Prevent applying the DOM `disabled`
    // attribute, which would make the element inaccessible. Prefer not to
    // manipulate consumer props but folks have proven that they're not reading
    // the docs.
    const a11ySafeDisabled = props.isDisabled && !props.isPending;

    const button = (
      <AriaButton
        {...props}
        isDisabled={a11ySafeDisabled}
        className={cn(
          buttonVariants({
            // static colours must render the same on any background, so the
            // surface layer adaptations don't apply
            layer: staticColor ? undefined : layer,
            prominence,
            size,
            staticColor,
          }),
          className
        )}
        ref={domRef}
        style={pressScale(domRef, style)}
      >
        {/* @ts-expect-error — this type would be inferred if inline but we have to share button context values */}
        <Provider values={contextValues}>
          {isReactText(children) ? <Text>{children}</Text> : children}

          {props.isPending && (
            <ProgressCircle
              aria-label=" (pending)"
              isIndeterminate
              size={size === 'large' ? 'medium' : 'small'}
              prominence="low"
              className="absolute"
            />
          )}
        </Provider>
      </AriaButton>
    );

    const racContext = useContext(AriaButtonContext);
    // Avoid errors being thrown when a slot is provided that's not defined by
    // some RAC provider for ButtonContext.
    if (
      props.slot &&
      racContext &&
      'slots' in racContext &&
      !racContext.slots?.[props.slot]
    ) {
      return (
        <AriaButtonContext.Provider value={null}>
          {button}
        </AriaButtonContext.Provider>
      );
    }

    return button;
  }
);

export function getButtonContextValues<P extends Omit<ButtonProps, 'children'>>(
  props: P
) {
  const { size = 'medium', isPending } = props;
  const pendingStyles = cn({ 'opacity-0': isPending });
  const textClassName = cn(textSizeClasses[size], pendingStyles);
  const renderIcon = centerBaseline({ className: pendingStyles });
  const adornmentSize = (
    {
      tiny: '2xsmall',
      small: 'xsmall',
      medium: 'xsmall',
      large: 'small',
    } as const
  )[size];

  const imageClassName = cn({
    'size-2xsmall': size === 'tiny',
    'size-xsmall': size === 'small' || size === 'medium',
    'size-small': size === 'large',
  });

  return [
    [TextContext, { className: textClassName, slot: 'text' }],
    [IconContext, { size: adornmentSize, render: renderIcon }],
    [ImageContext, { alt: '', className: imageClassName }],
    [EmblemContext, { alt: '', size: adornmentSize }],
    [ChainEmblemContext, { isDecorative: true, size: adornmentSize }],
  ];
}
